/**
 * 90天运营策略模拟组件 - 表格式紧凑版
 * 重构版本：使用 hooks 和 utils 分离逻辑，并拆分 UI 组件
 */
import React, { useState, useEffect } from 'react';
import { Card, Collapse, Divider, Typography } from 'antd';
import { StrategyPhase } from './ProfitScenario/types';
import { useStrategyPhases } from './OperationStrategy/hooks';
import { StrategyHeader } from './OperationStrategy/StrategyHeader';
import { StrategyPhaseList } from './OperationStrategy/StrategyPhaseList';
import { StrategyLogicExplanation } from './OperationStrategy/StrategyLogicExplanation';

const { Panel } = Collapse;
const { Text } = Typography;

interface OperationStrategySimulationProps {
    skuId?: number;
    landedCost: number;
    fbaFee: number;
    referralRate: number;
    storageFee: number;
    shippingCost: number; // 新增
    initialData?: StrategyPhase[];
    onDataChange?: (data: StrategyPhase[]) => void;
}

export const OperationStrategySimulation: React.FC<OperationStrategySimulationProps> = ({
    skuId,
    landedCost,
    fbaFee,
    referralRate,
    storageFee,
    shippingCost, // 新增
    initialData,
    onDataChange
}) => {
    const {
        phases,
        isLoading,
        updatePhaseInfo,
        updateWeekStrategy,
        addTask,
        updateTask,
        deleteTask,
        toggleTaskCompletion,
        clearCompletedTasks,
        addPhase,
        deletePhase,
        generateFullPlan
    } = useStrategyPhases(initialData);

    const [activeKeys, setActiveKeys] = useState<string[]>([]);

    // 数据加载完成后，默认展开所有阶段
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        if (!isLoading && phases.length > 0) {
            setActiveKeys(phases.map(p => p.id));
        }
    }, [isLoading]);

    // 当添加新阶段时，自动展开新阶段
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        if (phases.length > 0) {
            setActiveKeys(prev => {
                const newKeys = phases.map(p => p.id);
                // 找出不在 prev 中的新 ID
                const keysToAdd = newKeys.filter(id => !prev.includes(id));
                if (keysToAdd.length > 0) {
                    return [...prev, ...keysToAdd];
                }
                return prev;
            });
        }
    }, [phases.length]);

    // 数据同步到父组件
    useEffect(() => {
        if (onDataChange) {
            onDataChange(phases);
        }
    }, [phases, onDataChange]);

    const handleCollapseChange = (keys: string | string[]) => {
        setActiveKeys(typeof keys === 'string' ? [keys] : keys);
    };

    return (
        <Card bodyStyle={{ padding: '16px 24px' }}>
            <StrategyHeader
                phases={phases}
                landedCost={landedCost}
                fbaFee={fbaFee}
                referralRate={referralRate}
                storageFee={storageFee}
                generateFullPlan={generateFullPlan}
                addPhase={addPhase}
            />

            <StrategyPhaseList
                phases={phases}
                activeKeys={activeKeys}
                handleCollapseChange={handleCollapseChange}
                landedCost={landedCost}
                fbaFee={fbaFee}
                referralRate={referralRate}
                storageFee={storageFee}
                shippingCost={shippingCost}
                updatePhaseInfo={updatePhaseInfo}
                deletePhase={deletePhase}
                updateWeekStrategy={updateWeekStrategy}
                addTask={addTask}
                updateTask={updateTask}
                deleteTask={deleteTask}
                toggleTaskCompletion={toggleTaskCompletion}
                clearCompletedTasks={clearCompletedTasks}
            />

            <Divider />

            <Collapse ghost>
                <Panel header={<Text strong style={{ fontSize: 14 }}>📊 查看预估数据计算逻辑</Text>} key="logic">
                    <StrategyLogicExplanation />
                </Panel>
            </Collapse>
        </Card>
    );
};
